import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl) throw new Error('Missing REACT_APP_SUPABASE_URL');
if (!supabaseAnonKey) throw new Error('Missing REACT_APP_SUPABASE_PUBLISHABLE_KEY');

// TODO: Shorten this, but added 10 min timeout for testing log running flows
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    realtime: {
        timeout: 30 * 24 * 60 * 60 * 1000, // 30 days in ms
    },
});

// Export database types
export type Database = {
    public: {
        Tables: {
            profiles: {
                Row: {
                    id: string;
                    updated_at: string;
                    user_tag: string;
                    name: string;
                    profile_image_url: string;
                    bio: string;
                };
                Insert: {
                    id: string;
                    updated_at?: string;
                    user_tag?: string;
                    name?: string;
                    profile_image_url?: string;
                    bio?: string;
                };
                Update: {
                    id?: string;
                    updated_at?: string;
                    user_tag?: string;
                    name?: string;
                    profile_image_url?: string;
                    bio?: string;
                };
            };
            games: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    user_id: string;
                    game_name: string;
                    screenshot_url: string;
                    original_game_id?: string;
                    deleted: boolean;
                    hidden: boolean;
                };
                Insert: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    user_id?: string;
                    game_name: string;
                    screenshot_url: string;
                    original_game_id?: string;
                    deleted?: boolean;
                    hidden?: boolean;
                };
                Update: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    user_id?: string;
                    game_name?: string;
                    screenshot_url?: string;
                    deleted?: boolean;
                    hidden?: boolean;
                };
            };
            game_history_entries: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    game_id: string;
                    status: string;
                    prompt: string;
                    code: string;
                    // raw: string;
                    data: { plan?: string, stage?: string, stageType?: string; };
                    deleted: boolean;
                    user_facing: boolean;
                };
                Insert: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    game_id: string;
                    status: string;
                    prompt: string;
                    code: string;
                    // raw: string;
                    data?: { plan?: string, stage?: string, stageType?: string; };
                    deleted?: boolean;
                    user_facing?: boolean;
                };
                Update: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    game_id?: string;
                    status?: string;
                    prompt?: string;
                    code?: string;
                    // raw?: string;
                    data?: { plan?: string, stage?: string, stageType?: string; };
                    deleted?: boolean;
                    user_facing?: boolean;
                };
            };
            art: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    art_name: string;
                    user_id: string;
                    deleted: boolean | null;
                };
                Insert: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_name: string;
                    user_id?: string;
                    deleted?: boolean | null;
                };
                Update: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_name?: string;
                    user_id?: string;
                    deleted?: boolean | null;
                };
            };
            art_history_entries: {
                Row: {
                    iteration_id: string;
                    created_at: string;
                    updated_at: string;
                    art_id: string;
                    description: string;
                    user_svg: string;
                    user_png: string;
                    ai_image: string;
                    objects: string[];
                };
                Insert: {
                    iteration_id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_id: string;
                    description?: string;
                    user_svg?: string;
                    user_png?: string;
                    ai_image?: string;
                    objects?: string[];
                };
                Update: {
                    iteration_id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_id?: string;
                    description?: string;
                    user_svg?: string;
                    user_png?: string;
                    ai_image?: string;
                    objects?: string[];
                };
            };
            art_3d: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    art_name: string;
                    user_id: string;
                    deleted: boolean | null;
                };
                Insert: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_name: string;
                    user_id?: string;
                    deleted?: boolean | null;
                };
                Update: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_name?: string;
                    user_id?: string;
                    deleted?: boolean | null;
                };
            };
            art_3d_history_entries: {
                Row: {
                    iteration_id: string;
                    created_at: string;
                    updated_at: string;
                    art_id: string;
                    description: string;
                    user_svg: string;
                    user_png: string;
                    ai_image: string;
                    ai_3d_model: string;
                    objects: string[];
                };
                Insert: {
                    iteration_id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_id: string;
                    description?: string;
                    user_svg?: string;
                    user_png?: string;
                    ai_image?: string;
                    ai_3d_model?: string;
                    objects?: string[];
                };
                Update: {
                    iteration_id?: string;
                    created_at?: string;
                    updated_at?: string;
                    art_id?: string;
                    description?: string;
                    user_svg?: string;
                    user_png?: string;
                    ai_image?: string;
                    ai_3d_model?: string;
                    objects?: string[];
                };
            };
            game_art: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    game_id: string;
                    asset_name: string;
                    asset_url: string;
                    code_file_name: string;
                    asset_description: string;
                    asset_source: string;
                    art_id: string;
                };
                Insert: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    game_id: string;
                    asset_name?: string;
                    asset_url?: string;
                    code_file_name?: string;
                    asset_description?: string;
                    asset_source?: string;
                    art_id?: string;
                };
                Update: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    game_id?: string;
                    asset_name?: string;
                    asset_url?: string;
                    code_file_name?: string;
                    asset_description?: string;
                    asset_source?: string;
                    art_id?: string;
                };
            };
            animation: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    animation_name: string;
                    user_id: string | null;
                    deleted: boolean | null;
                    hidden: boolean | null;
                    private: boolean | null;
                };
                Insert: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    animation_name?: string;
                    user_id: string;
                    deleted?: boolean | null;
                    hidden?: boolean | null;
                    private?: boolean | null;
                };
                Update: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    animation_name?: string;
                    user_id?: string | null;
                    deleted?: boolean | null;
                    hidden?: boolean | null;
                    private?: boolean | null;
                };
            };
            animation_history_entries: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    animation_id: string;
                    user_prompt: string;
                    enhanced_prompt: string;
                    reference_image: string;
                    reference_image_description: string;
                    animation_video: string;
                    animation_images: string[];
                };
                Insert: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    animation_id: string;
                    user_prompt?: string;
                    enhanced_prompt?: string;
                    reference_image?: string;
                    reference_image_description?: string;
                    animation_video?: string;
                    animation_images?: string[];
                };
                Update: {
                    id?: string;
                    created_at?: string;
                    updated_at?: string;
                    animation_id?: string;
                    user_prompt?: string;
                    enhanced_prompt?: string;
                    reference_image?: string;
                    reference_image_description?: string;
                    animation_video?: string;
                    animation_images?: string[];
                };
            };
            credits_usage: {
                Row: {
                    id: string;
                    created_at: string;
                    updated_at: string;
                    user_id: string;
                    subscription: string | null;
                    free_credits_received_this_month: BigInteger;
                    free_credits_used_this_month: BigInteger;
                    paid_credits_received_this_month: BigInteger;
                    paid_credits_used_this_month: BigInteger;
                    flex_credits_available: BigInteger;
                    flex_credits_used_this_month: BigInteger;
                    is_new_sign_up: boolean | null;
                    last_daily_gift_at: string | null;
                    last_monthly_reset_at: string | null;
                    stripe_customer_id: string | null;
                };
            };
            game_metrics: {
                Row: {
                    id: string;
                    game_id: string;
                    created_at: string;
                    updated_at: string;
                    plays: number;
                    likes: number;
                    remixes: number;
                    is_pinned: boolean;
                };
                Insert: {
                    id?: string;
                    game_id?: string;
                    created_at?: string;
                    updated_at?: string;
                    plays?: number;
                    likes?: number;
                    remixes?: number;
                    is_pinned?: boolean;
                };
                Update: {
                    id?: string;
                    game_id?: string;
                    created_at?: string;
                    updated_at?: string;
                    plays?: number;
                    likes?: number;
                    remixes?: number;
                    is_pinned?: boolean;
                };
            };
            github_account: {
                Row: {
                  id: string;
                  created_at: string;
                  updated_at: string;
                  user_id: string;
                  github_user_id: number | null;
                  github_username: string | null;
                  github_access_token: string | null;
                  github_refresh_token: string | null;
                  token_expires_at: string | null;
                };
                Insert: {
                  id?: string;
                  created_at?: string;
                  updated_at?: string;
                  user_id?: string;
                  github_user_id?: number | null;
                  github_username?: string | null;
                  github_access_token?: string | null;
                  github_refresh_token?: string | null;
                  token_expires_at?: string | null;
                };
                Update: {
                  id?: string;
                  created_at?: string;
                  updated_at?: string;
                  user_id?: string;
                  github_user_id?: number | null;
                  github_username?: string | null;
                  github_access_token?: string | null;
                  github_refresh_token?: string | null;
                  token_expires_at?: string | null;
                };
              };
              github_repo: {
                Row: {
                  id: string;
                  created_at: string;
                  updated_at: string;
                  user_id: string;
                  game_id: string;
                  repo_name: string;
                  status: string;
                };
                Insert: {
                  id?: string;
                  created_at?: string;
                  updated_at?: string;
                  user_id?: string;
                  game_id?: string;
                  repo_name?: string;
                  status?: string;
                };
                Update: {
                  id?: string;
                  created_at?: string;
                  updated_at?: string;
                  user_id?: string;
                  game_id?: string;
                  repo_name?: string;
                  status?: string;
                };
              };
        };
    };
};